import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChatCircleText, ListChecks, CalendarBlank, CheckCircle, Circle, PlayCircle, Play, ArrowCounterClockwise, PaperPlaneRight, ArrowSquareOut, Eye, EyeSlash, Checks, Check } from "@phosphor-icons/react";
import { api, fmtDateTime, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { InitialsBadge } from "./InitialsBadge";

const TASK_STEPS = [
  { id: "todo", label: "To do", Icon: Circle },
  { id: "started", label: "Started", Icon: PlayCircle },
  { id: "done", label: "Done", Icon: CheckCircle },
];
const STEP_TONE = { todo: "bg-slate-100 text-slate-600 border-slate-200", started: "bg-amber-50 text-amber-700 border-amber-200", done: "bg-emerald-50 text-emerald-700 border-emerald-200" };

export const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const isOverdue = (m) => m.kind === "task" && m.due_at && m.task_status !== "done" && m.due_at < localToday();
export const fmtDue = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

export const ticketHref = (id, { note, task } = {}) =>
  `/repairs/${id}${note ? `?note=${note}` : task ? `?task=${task}` : ""}`;

export function TicketChip({ id, number, customer, note, task }) {
  if (!id) return null;
  return (
    <Link to={ticketHref(id, { note, task })} onClick={(e) => e.stopPropagation()} data-testid={`inbox-ticket-link-${number}`}
      className="inline-flex items-center gap-1 h-6 px-2 rounded-md border border-[#BFDBFE] bg-[#EFF6FF] text-[11px] font-semibold text-[#1E5FEB] hover:bg-[#DBEAFE] transition-colors">
      <span className="font-mono">{number}</span>{customer ? <span className="font-normal text-slate-500 truncate max-w-[120px]">· {customer}</span> : null}
      <ArrowSquareOut size={11} weight="bold" />
    </Link>
  );
}

function SeenTick({ seen, testId }) {
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10.5px] font-semibold ${seen ? "text-[#1E5FEB]" : "text-slate-400"}`} data-testid={testId}>
      {seen ? <Checks size={12} weight="bold" /> : <Check size={12} weight="bold" />}{seen ? "Seen" : "Sent"}
    </span>
  );
}

export function TypingDots({ name }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-slate-500" data-testid="typing-indicator">
      <span className="inline-flex gap-0.5 px-2.5 py-2 rounded-xl bg-slate-100">
        {[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
      </span>
      {name} is typing…
    </div>
  );
}

export function InboxThread({ m, me, actor = me, onChange, showSentStatus, initialOpen = false, typingName = null, fadeOnDone = false }) {
  const [open, setOpen] = useState(initialOpen);
  useEffect(() => { if (initialOpen) setOpen(true); }, [initialOpen]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const isRecipient = actor === m.to_id;
  const isParticipant = isRecipient || actor === m.from_id;
  const unread = (m.unread_for || []).includes(me);
  const counterpart = me === m.from_id ? m.to_name : m.from_name;
  const otherId = actor === m.from_id ? m.to_id : m.from_id;
  const otherSeen = (m.seen_at || {})[otherId] || (otherId === m.to_id ? m.read_at : null);
  const lastPing = useRef(0);
  const onReplyChange = (v) => {
    setReply(v);
    if (v && Date.now() - lastPing.current > 2500) {
      lastPing.current = Date.now();
      api.post(`/inbox/messages/${m.id}/typing`).catch(() => {});
    }
  };
  useEffect(() => {
    if (initialOpen && unread) api.post(`/inbox/messages/${m.id}/read`).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (fn) => {
    setBusy(true);
    try { const r = await fn(); onChange?.(r?.data); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const toggle = () => {
    setOpen((o) => !o);
    if (!open && unread && actor === me) api.post(`/inbox/messages/${m.id}/read`).then(() => onChange?.()).catch(() => {});
  };
  const sendReply = () => reply.trim() && run(async () => { const r = await api.post(`/inbox/messages/${m.id}/reply`, { text: reply }); setReply(""); return r; });
  const [justDone, setJustDone] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try {
      const r = await api.post(`/inbox/messages/${m.id}/task-status`, { status });
      if (status === "done") {
        setJustDone(true);
        toast.success("Task done", { description: "Moved to the Done tab" });
        setTimeout(() => { setJustDone(false); onChange?.(r.data); }, 1600);
      } else { setJustDone(false); onChange?.(r.data); }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const shownStatus = justDone ? "done" : m.task_status;

  return (
    <div className={`rounded-xl border transition-colors ${justDone ? `border-emerald-300 bg-emerald-50 ${fadeOnDone ? "rh-done-out" : ""}` : unread ? "border-[#BFDBFE] bg-[#F5F9FF]" : "border-[#E5E7EB] bg-white"} ${m.done && !showSentStatus ? "opacity-70" : ""}`} data-testid={`inbox-thread-${m.id}`}>
      <div className="flex items-start">
      <button onClick={toggle} className="flex-1 min-w-0 text-left px-4 py-3 flex items-start gap-3" data-testid={`inbox-thread-toggle-${m.id}`}>
        <InitialsBadge name={counterpart} size={34} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-slate-500">
            <span className="font-semibold text-slate-800">{me === m.from_id ? `To ${m.to_name}` : m.from_name}</span>
            {m.kind === "task"
              ? <span className={`inline-flex items-center gap-1 h-5 px-1.5 rounded border text-[10px] font-bold uppercase tracking-wider ${STEP_TONE[shownStatus]}`} data-testid={`inbox-task-status-${m.id}`}><ListChecks size={11} weight="bold" />Task · {TASK_STEPS.find((s) => s.id === shownStatus)?.label}</span>
              : <span className="inline-flex items-center gap-1 text-[11px]"><ChatCircleText size={12} weight="bold" />Message</span>}
            {m.due_at && <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${isOverdue(m) ? "text-rose-600" : "text-slate-500"}`} data-testid={`inbox-due-${m.id}`}><CalendarBlank size={11} weight="bold" />{isOverdue(m) ? "Overdue · " : "Due "}{fmtDue(m.due_at)}</span>}
            <span className="text-slate-400">· {fmtDateTime(m.updated_at || m.created_at)}</span>
            {unread && <span className="w-2 h-2 rounded-full bg-[#1E5FEB]" data-testid={`inbox-unread-dot-${m.id}`} />}
          </div>
          <div className={`mt-1 text-[13.5px] text-slate-900 break-words ${open ? "whitespace-pre-wrap" : "line-clamp-2"}`}>{m.text}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <TicketChip id={m.ticket_id} number={m.ticket_number} customer={m.customer_name} task={m.kind === "task" ? m.id : null} />
            {m.replies?.length > 0 && <span className="text-[11px] text-slate-500">{m.replies.length} repl{m.replies.length === 1 ? "y" : "ies"}</span>}
            {showSentStatus && (
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${m.read_at ? "text-emerald-600" : "text-slate-400"}`} data-testid={`inbox-read-status-${m.id}`}>
                {m.read_at ? <><Eye size={12} weight="bold" />Read {fmtDateTime(m.read_at)}</> : <><EyeSlash size={12} weight="bold" />Not read yet</>}
              </span>
            )}
          </div>
        </div>
      </button>
      {isParticipant && m.kind === "task" && (
        <div className="shrink-0 pr-4 pt-3 flex gap-1.5" data-testid={`inbox-task-quick-${m.id}`}>
          {shownStatus === "todo" && (
            <button disabled={busy} onClick={() => setStatus("started")} data-testid={`inbox-task-quick-start-${m.id}`} title="Start this task"
              className="h-8 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-slate-700 inline-flex items-center gap-1.5 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 transition-colors">
              <Play size={12} weight="fill" />Start
            </button>
          )}
          {shownStatus !== "done" ? (
            <button disabled={busy} onClick={() => setStatus("done")} data-testid={`inbox-task-quick-done-${m.id}`} title="Mark as done"
              className="h-8 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-slate-700 inline-flex items-center gap-1.5 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
              <Circle size={13} weight="bold" />Mark done
            </button>
          ) : (
            <>
              <span className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-[12px] font-bold inline-flex items-center gap-1.5" data-testid={`inbox-task-done-badge-${m.id}`}>
                <CheckCircle size={13} weight="fill" />Done
              </span>
              <button disabled={busy} onClick={() => setStatus("todo")} data-testid={`inbox-task-quick-undo-${m.id}`} title="Move back to To do"
                className="h-8 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-slate-500 inline-flex items-center gap-1.5 hover:bg-slate-50 transition-colors">
                <ArrowCounterClockwise size={12} weight="bold" />Undo
              </button>
            </>
          )}
        </div>
      )}
      </div>
      {open && (
        <div className="px-4 pb-4 pl-[62px] space-y-3">
          {actor === m.from_id && <SeenTick seen={!!otherSeen} testId={`seen-original-${m.id}`} />}
          {m.replies?.map((r) => (
            <div key={r.id} className="flex items-start gap-2" data-testid={`inbox-reply-${r.id}`}>
              <InitialsBadge name={r.from_name} size={24} />
              <div className="rounded-xl bg-slate-50 border border-[#F1F5F9] px-3 py-2 text-[13px] text-slate-800 whitespace-pre-wrap">
                <div className="text-[10.5px] text-slate-500 mb-0.5"><b className="text-slate-700">{r.from_name}</b> · {fmtDateTime(r.created_at)}</div>{r.text}
                {r.from_id === actor && isParticipant && <div className="mt-1 text-right"><SeenTick seen={!!otherSeen && otherSeen >= r.created_at} testId={`seen-reply-${r.id}`} /></div>}
              </div>
            </div>
          ))}
          {typingName && <TypingDots name={typingName} />}
          {isParticipant && m.kind === "task" && (
            <div className="flex flex-wrap gap-1.5" data-testid={`inbox-task-steps-${m.id}`}>
              {TASK_STEPS.map(({ id, label, Icon }) => (
                <button key={id} disabled={busy || shownStatus === id} onClick={() => setStatus(id)}
                  data-testid={`inbox-task-set-${id}-${m.id}`}
                  className={`h-8 px-3 rounded-lg border text-[12px] font-semibold inline-flex items-center gap-1.5 transition-colors ${shownStatus === id ? STEP_TONE[id] : "border-[#E5E7EB] bg-white text-slate-600 hover:border-[#BFDBFE] hover:text-[#1E5FEB]"}`}>
                  <Icon size={13} weight="bold" />{label}
                </button>
              ))}
            </div>
          )}
          {isParticipant && (
            <div className="flex items-end gap-2">
              <textarea rows={1} value={reply} onChange={(e) => onReplyChange(e.target.value)} placeholder="Write a reply…"
                onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); sendReply(); } }}
                data-testid={`inbox-reply-input-${m.id}`}
                className="flex-1 min-h-[38px] max-h-32 resize-y rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-[13px] focus:outline-none focus:border-[#1E5FEB]" />
              <button onClick={sendReply} disabled={busy || !reply.trim()} data-testid={`inbox-reply-send-${m.id}`}
                className="h-[38px] px-3.5 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-slate-200 disabled:text-slate-400 text-white text-[12px] font-bold inline-flex items-center gap-1.5 transition-colors">
                <PaperPlaneRight size={13} weight="fill" /> Reply
              </button>
            </div>
          )}
          {isRecipient && m.kind === "message" && (
            <button onClick={() => run(() => api.post(`/inbox/messages/${m.id}/done`, { done: !m.done }))} disabled={busy}
              data-testid={`inbox-done-${m.id}`}
              className={`h-8 px-3 rounded-lg border text-[12px] font-semibold inline-flex items-center gap-1.5 transition-colors ${m.done ? "border-[#E5E7EB] text-slate-600 hover:bg-slate-50" : "border-[#E5E7EB] bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"}`}>
              <Circle size={13} weight="bold" />{m.done ? "Move back to To do" : "Mark as done"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
